/**
 * Responsive shell: sidebar on desktop, bottom tab bar on phones (thumb
 * reach), slim top bar with the sync indicator. Capture screens hide the tab
 * bar to make room for their own bottom action bar.
 */
import type { ReactNode } from 'react';
import { Avatar, Banner, BrandMark, Icon, SyncIndicator } from '../components';
import type { AppVersionPolicy } from '../lib/api/types';
import { assetUrl, cachedBranding, withSignedInBranding } from '../lib/branding';
import { cx } from '../lib/cx';
import { useI18n } from '../lib/i18n';
import { Link, NavLink, useLocation, useNavigate } from '../lib/router';
import type { VersionStatus } from '../lib/version';
import { UpdateAvailableBanner } from '../features/auth/GateScreens';
import { useOnline, useSyncStatus } from './hooks';
import { useMe } from './me';
import { NAV } from './nav';
import { useServices } from './services';

export function useSyncLabels() {
  const { t } = useI18n();
  const s = useSyncStatus();
  return {
    status: s,
    labels: {
      synced: t('sync.allSynced'),
      pending: t('sync.pending', { count: s.pending }),
      offline: t('ui.sync.offline'),
      problem: t('ui.sync.problem', { count: s.rejected }),
      syncing: t('ui.sync.syncing'),
    },
  };
}

export function ConnectedSyncIndicator({ compact }: { compact?: boolean }) {
  const { status, labels } = useSyncLabels();
  const online = useOnline();
  const navigate = useNavigate();
  return (
    <SyncIndicator
      pending={status.pending}
      rejected={status.rejected}
      online={online && status.online}
      flushing={status.flushing}
      stale={status.stale}
      labels={labels}
      compact={compact}
      onClick={() => navigate('/profile#sync')}
    />
  );
}

function Brand() {
  const { api } = useServices();
  const { me } = useMe();
  const b = withSignedInBranding(cachedBranding(), me.settings);
  // The header shows the square icon next to the name (a full logo would repeat it).
  const icon = assetUrl(api.baseUrl, b.iconAssetId);
  return (
    <Link to="/" className="flex min-h-11 items-center gap-2.5 font-display text-base font-bold tracking-tight text-fg">
      {icon ? (
        <img src={icon} alt="" className="size-8 rounded-lg object-contain" />
      ) : (
        <BrandMark letter={b.systemName.slice(0, 1)} size="sm" />
      )}
      <span className="truncate">{b.systemName}</span>
    </Link>
  );
}

export function AppShell({ children, versionStatus, versionPolicy }: { children: ReactNode; versionStatus: VersionStatus; versionPolicy: AppVersionPolicy | null }) {
  const m = useMe();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const sync = useSyncStatus();
  const online = useOnline();
  const items = NAV.filter((n) => n.show(m));
  const tabs = items.filter((n) => n.phoneTab);
  const focusMode = pathname.startsWith('/capture/');

  return (
    <div className="flex min-h-full">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-e border-border bg-surface px-3.5 py-4 lg:flex">
        <div className="border-b border-border/80 px-1.5 pb-3.5">
          <Brand />
        </div>
        <nav className="mt-4 flex flex-1 flex-col gap-1" aria-label={t('ui.nav.main')}>
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cx(
                  'relative flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm font-semibold tracking-tight transition-all duration-150',
                  isActive
                    ? 'bg-brand-soft text-brand shadow-2xs ring-1 ring-inset ring-brand/15 before:absolute before:inset-y-2 before:start-0 before:w-[3px] before:rounded-full before:bg-brand'
                    : 'text-muted hover:bg-surface-2/80 hover:text-fg',
                )
              }
            >
              <Icon name={n.icon} size={18} />
              {t(n.labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="flex flex-col gap-2.5 border-t border-border pt-3.5">
          <ConnectedSyncIndicator />
          <Link to="/profile" className="flex items-center gap-3 rounded-xl border border-border/80 bg-surface-2/45 p-2 transition-colors hover:border-border-strong hover:bg-surface-2">
            <Avatar name={m.me.user.name || m.me.user.email} size={34} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold tracking-tight">{m.me.user.name}</span>
              <span className="block truncate font-mono text-[11px] text-muted">{m.me.user.email}</span>
            </span>
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Phone top bar */}
        {!focusMode && (
          <header className="pt-safe sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border bg-surface/90 px-4 py-1.5 backdrop-blur-md lg:hidden">
            <Brand />
            <ConnectedSyncIndicator compact />
          </header>
        )}

        <div className="flex flex-col gap-2 px-4 pt-3 empty:hidden lg:px-8">
          {versionStatus === 'update-available' && <UpdateAvailableBanner policy={versionPolicy} />}
          {(m.offline || !online) && !focusMode && <Banner tone="warning">{t('ui.offline.banner')}</Banner>}
          {sync.stale && (
            <Banner tone="danger" title={t('ui.sync.staleTitle')}>
              {t('sync.staleWarning')}
            </Banner>
          )}
        </div>

        <main className={cx('mx-auto w-full max-w-6xl flex-1 px-4 py-4 lg:px-8 lg:py-6', !focusMode && 'pb-28 lg:pb-6')}>{children}</main>

        {/* Phone bottom tab bar */}
        {!focusMode && (
          <nav
            aria-label={t('ui.nav.main')}
            className="pb-safe fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-surface/95 backdrop-blur-md lg:hidden"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cx(
                    'relative flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold tracking-tight transition-colors',
                    isActive
                      ? 'text-brand before:absolute before:top-0 before:h-[2.5px] before:w-7 before:rounded-b-full before:bg-brand'
                      : 'text-muted hover:text-fg',
                  )
                }
              >
                <Icon name={n.icon} size={22} />
                {t(n.labelKey)}
                {n.to === '/profile' && (sync.pending > 0 || sync.rejected > 0) && (
                  <span className={cx('absolute top-2 ms-6 size-2 rounded-full ring-2 ring-surface', sync.rejected > 0 ? 'bg-danger' : 'bg-info')} aria-hidden="true" />
                )}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}

/** Standard page header: title, optional back link and actions. */
export function PageHeader({ title, subtitle, back, actions }: { title: ReactNode; subtitle?: ReactNode; back?: string; actions?: ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="mb-5 flex items-start gap-3 border-b border-border/80 pb-4">
      {back && (
        <button
          type="button"
          onClick={() => navigate(back)}
          aria-label={t('ui.common.back')}
          className="tap -ms-2 flex items-center justify-center rounded-lg border border-transparent hover:border-border hover:bg-surface-2"
        >
          <Icon name="chevronLeft" size={22} className="rtl:rotate-180" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <h1 className="font-display text-2xl font-bold tracking-tight leading-tight text-fg">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
